import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  FlatList,
  Pressable,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useFocusEffect } from "@react-navigation/native";
import { notifySuccess, notifyError } from "@components/ds/notify";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import {
  getDsRoutingDbTps,
  markDsRoutingDbOfd,
  unassignDsRoutingTps,
} from "@services/api/dsRouting";
import RoutingHeader from "../components/RoutingHeader";
import RoutingSearch from "../components/RoutingSearch";
import { useRoutingSearch } from "../useRoutingSearch";
import DbTpRow from "../components/DbTpRow";
import ConfirmPopup from "../components/ConfirmPopup";
import {
  filterTps,
  getCardActions,
  getHandoffConflict,
  getOfdReadyKeys,
  getSelectedCxIds,
  getTpKey,
  pruneSelection,
  routingErrorKey,
  summarizeOfdResult,
  toggleKey,
} from "../helpers";
import { C, RText, tpName } from "../theme";

const PATH = "DsRouting";

const CONFIRM = {
  UNASSIGN: "UNASSIGN",
  HANDOFF_UNASSIGN: "HANDOFF_UNASSIGN",
  OFD: "OFD",
};

// Registered as `DsRoutingDbTps`; opened from a DsRoutingDbList row with
// `{ cp_db_id, name }`. The DB's cards: tick the ones that are ready and
// Mark OFD (N) in one go, or Remove (un-assign) a card that isn't locked.
// Reloads on focus and after every action; a partial Mark OFD shows the
// failures in a banner above the list.
const DsRoutingDbTps = ({ navigation, route }) => {
  const cpDbId = route?.params?.cp_db_id;
  const insets = useSafeAreaInsets();
  const [name, setName] = useState(route?.params?.name || "");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [failures, setFailures] = useState([]);
  const search = useRoutingSearch();

  const reload = useCallback(
    async ({ refreshing = false } = {}) => {
      try {
        if (refreshing) setIsRefreshing(true);
        else setIsLoading(true);
        const response = await getDsRoutingDbTps(cpDbId);
        const next = response?.data?.data || null;
        setData(next);
        if (next?.name) setName(next.name);
        // A card that stopped being ready drops out of the selection.
        setSelected((keys) => pruneSelection(next?.tps || [], keys));
      } catch (error) {
        showApiError(error, routingErrorKey);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [cpDbId],
  );

  useFocusEffect(
    useCallback(() => {
      reload();
    }, [reload]),
  );

  const tps = useMemo(() => data?.tps || [], [data]);
  const readyKeys = useMemo(() => getOfdReadyKeys(tps), [tps]);
  const cxIds = useMemo(() => getSelectedCxIds(tps, selected), [tps, selected]);
  // Search narrows the cards shown. Ticks on cards it hides stay, and Mark
  // OFD (N) counts every ticked card; Select all works on the shown ones.
  const shown = useMemo(
    () => filterTps(tps, search.query),
    [tps, search.query],
  );
  const shownReadyKeys = useMemo(() => getOfdReadyKeys(shown), [shown]);
  const allSelected =
    shownReadyKeys.length > 0 &&
    shownReadyKeys.every((key) => selected.includes(key));
  const onSelectAll = () =>
    setSelected((keys) =>
      allSelected
        ? keys.filter((key) => !shownReadyKeys.includes(key))
        : Array.from(new Set(keys.concat(shownReadyKeys))),
    );

  const onConfirm = async () => {
    const { type, tp } = confirm;
    let swapped = false;
    try {
      setSubmitting(true);
      if (type === CONFIRM.UNASSIGN || type === CONFIRM.HANDOFF_UNASSIGN) {
        const response = await unassignDsRoutingTps({
          cx_ids: [tp.cx_id],
          cp_db_id: cpDbId,
          confirm_handoff: type === CONFIRM.HANDOFF_UNASSIGN,
        });
        const conflict = getHandoffConflict(response);
        if (conflict) {
          // Stale list: swap to the hand-off wording; the next tap retries.
          setConfirm({
            type: CONFIRM.HANDOFF_UNASSIGN,
            tp,
            handoffDb: conflict.db_name || name,
          });
          swapped = true;
          return;
        }
        notifySuccess(t(`${PATH}/toast/unassigned`));
      } else {
        const response = await markDsRoutingDbOfd({
          cp_db_id: cpDbId,
          cx_ids: cxIds,
        });
        const result = summarizeOfdResult(response?.data?.data);
        setFailures(result.codes);
        if (result.failedCount === 0) {
          notifySuccess(
            t(`${PATH}/byDb/ofdDone`, { count: String(result.doneCount) }),
          );
        } else if (result.doneCount === 0) {
          notifyError(
            t(`${PATH}/byDb/ofdFailed`, { count: String(result.failedCount) }),
            "",
          );
        } else {
          notifyError(
            t(`${PATH}/byDb/ofdPartial`, {
              done: String(result.doneCount),
              failed: String(result.failedCount),
            }),
            "",
          );
        }
      }
    } catch (error) {
      showApiError(error, routingErrorKey);
    } finally {
      setSubmitting(false);
      if (!swapped) {
        setConfirm(null);
        reload();
      }
    }
  };

  const renderItem = ({ item }) => (
    <DbTpRow
      item={item}
      today={data?.date}
      selected={selected.includes(getTpKey(item))}
      onToggle={(tp) => setSelected((keys) => toggleKey(keys, getTpKey(tp)))}
      onRemove={(tp) =>
        setConfirm({
          type: getCardActions(tp).requiresHandoffConfirm
            ? CONFIRM.HANDOFF_UNASSIGN
            : CONFIRM.UNASSIGN,
          tp,
          handoffDb: name,
        })
      }
    />
  );

  const renderHeader = () => (
    <View>
      {failures.length > 0 ? (
        <View testID="ds-routing-db-failures" style={styles.banner}>
          {failures.map((code) => (
            <RText key={code} size={12} weight={600} color={C.redText}>
              {t(routingErrorKey(code))}
            </RText>
          ))}
        </View>
      ) : null}
      {shownReadyKeys.length > 0 ? (
        <View style={styles.selectRow}>
          <RText size={12} weight={600} color={C.muted}>
            {t(`${PATH}/byDb/selected`, { count: String(cxIds.length) })}
          </RText>
          <Pressable
            testID="ds-routing-db-select-all"
            onPress={onSelectAll}
            hitSlop={8}
          >
            <RText size={12} weight={700} color={C.blueLine}>
              {t(`${PATH}/byDb/${allSelected ? "clear" : "selectAll"}`)}
            </RText>
          </Pressable>
        </View>
      ) : null}
    </View>
  );

  const confirmTp = confirm?.tp;
  const isHandoff = confirm?.type === CONFIRM.HANDOFF_UNASSIGN;
  const isUnassign = confirm?.type === CONFIRM.UNASSIGN || isHandoff;
  const ofdTitle = t(
    `${PATH}/byDb/${cxIds.length === 1 ? "confirmOfdOne" : "confirmOfd"}`,
    { count: String(cxIds.length), db: name },
  );

  return (
    <View style={styles.screen}>
      <RoutingHeader
        title={t(`${PATH}/byDb/tpsTitle`, { name })}
        navigation={navigation}
      />
      <RoutingSearch
        testID="ds-routing-db-tps-search"
        value={search.text}
        onChangeText={search.setText}
        placeholder={t(`${PATH}/searchPlaceholder`)}
      />
      {isLoading && tps.length === 0 ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <FlatList
          testID="ds-routing-db-tps"
          showsVerticalScrollIndicator={false}
          data={shown}
          keyExtractor={getTpKey}
          renderItem={renderItem}
          ListHeaderComponent={renderHeader}
          refreshing={isRefreshing}
          onRefresh={() => reload({ refreshing: true })}
          contentContainerStyle={styles.listBody}
          ListEmptyComponent={
            isLoading ? null : (
              <RText
                size={14}
                weight={500}
                color={C.muted}
                style={styles.empty}
              >
                {t(search.query ? `${PATH}/noMatch` : `${PATH}/byDb/tpsEmpty`)}
              </RText>
            )
          }
        />
      )}

      {readyKeys.length > 0 ? (
        <View
          style={[styles.footer, { paddingBottom: 12 + (insets.bottom || 0) }]}
        >
          <Pressable
            testID="ds-routing-db-mark-ofd"
            disabled={cxIds.length === 0 || submitting}
            onPress={() => setConfirm({ type: CONFIRM.OFD })}
            style={[styles.cta, cxIds.length === 0 && styles.ctaDisabled]}
          >
            <RText
              size={15}
              weight={600}
              color={cxIds.length === 0 ? C.disabledInk : "#FFFFFF"}
            >
              {cxIds.length === 0
                ? t(`${PATH}/byDb/markOfdNone`)
                : t(`${PATH}/byDb/markOfd`, { count: String(cxIds.length) })}
            </RText>
          </Pressable>
        </View>
      ) : null}

      <ConfirmPopup
        visible={Boolean(confirm)}
        title={
          isHandoff
            ? t(`${PATH}/handoff/unassign`, {
                db: confirm?.handoffDb || name,
              })
            : isUnassign
              ? t(`${PATH}/confirmUnassign`, {
                  name: confirmTp ? tpName(confirmTp) : "",
                  db: name,
                })
              : ofdTitle
        }
        confirmLabel={t(
          isUnassign ? `${PATH}/actions/remove` : `${PATH}/actions/markOfd`,
        )}
        cancelLabel={t(`${PATH}/cancel`)}
        confirmColor={isUnassign ? C.red : C.green}
        loading={submitting}
        onCancel={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
    </View>
  );
};

export default DsRoutingDbTps;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  listBody: { paddingTop: 12, paddingBottom: 24 },
  banner: {
    backgroundColor: C.redBg,
    borderRadius: 10,
    padding: 12,
    gap: 4,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  selectRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingBottom: 10,
  },
  empty: { textAlign: "center", marginTop: 60 },
  footer: {
    backgroundColor: C.surface,
    borderTopWidth: 1,
    borderTopColor: C.border,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  cta: {
    backgroundColor: C.green,
    borderRadius: 9,
    paddingVertical: 12,
    alignItems: "center",
  },
  ctaDisabled: { backgroundColor: C.disabledBg },
});
